// client/src/components/common/HeaderSearchBox.jsx
//
// Header search field with debounced customer lookup:
//   [🔍]  (555) 123-4567                                   [✕]
//   └── summary row / loading / no-results / error dropdown
//
// Pausing typing for 300ms fires a lookup against POST /api/search-phone.
// A match populates the summary row; clicking it (or pressing Enter) commits
// the search via onSearch, which opens the full customer screen.
//
// NOTE: the backend lookup is phone-only — there is no email/name search
// endpoint — so lookups only fire once 10 digits have been entered.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { colors, radii } from '../../../theme/tokens';
import { apiClient } from '../../../services/apiClient';

const DEBOUNCE_MS = 300;

const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Reduce a /api/search-phone payload to a single summary row.
 * Returns null when nothing matched (the endpoint always 200s, signalling a
 * miss via the has*Data flags rather than a 404).
 */
export const buildSummary = (payload) => {
  if (!payload?.success) return null;

  const { hasVinData, hasTekionData, hasTwilioData, data } = payload;
  if (!hasVinData && !hasTekionData && !hasTwilioData) return null;

  const tekion = data?.tekion || {};
  const contact = data?.vinSolutions?.contact || {};
  const twilio = data?.twilio || {};

  const tekionName =
    [tekion.firstName, tekion.lastName].filter(Boolean).join(' ').trim() ||
    tekion.displayName;

  const name = tekionName || contact.fullName || twilio.callerName || 'Unknown Caller';

  const sources = [];
  if (hasVinData) sources.push('CRM');
  if (hasTekionData) sources.push('DMS');
  if (!hasVinData && !hasTekionData && hasTwilioData) sources.push('Caller ID');

  return {
    name,
    phone: twilio.nationalFormat || contact.phone || tekion.primaryPhone || payload.phoneNumber,
    email: contact.email || tekion.email || '',
    sources,
  };
};

const HeaderSearchBox = ({ onSearch, isSearching }) => {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | found | no-results | error
  const [summary, setSummary] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const inputRef = useRef(null);
  const containerRef = useRef(null);
  // Guards against out-of-order responses: only the newest request may commit.
  const requestIdRef = useRef(0);

  const digits = query.replace(/\D/g, '');

  const runLookup = useCallback(async (phoneDigits) => {
    const requestId = ++requestIdRef.current;
    setStatus('loading');
    setSummary(null);
    setIsOpen(true);

    try {
      const response = await apiClient.post('/search-phone', { phoneNumber: phoneDigits });
      if (requestId !== requestIdRef.current) return; // a newer lookup superseded this one

      const match = buildSummary(response.data);
      if (match) {
        setSummary(match);
        setStatus('found');
      } else {
        setStatus('no-results');
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error('Customer lookup failed:', err);
      setStatus('error');
    }
  }, []);

  // Debounced lookup — fires 300ms after typing stops, once we have 10 digits.
  useEffect(() => {
    if (digits.length !== 10) {
      requestIdRef.current++; // cancel any in-flight result
      setStatus('idle');
      setSummary(null);
      setIsOpen(false);
      return;
    }

    const timer = setTimeout(() => runLookup(digits), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [digits, runLookup]);

  // Close the dropdown on outside click or Escape.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleInputChange = (e) => {
    setQuery(formatPhoneNumber(e.target.value));
  };

  // Enter commits the search (opens the full customer screen).
  const handleSubmit = (e) => {
    e.preventDefault();
    if (digits.length !== 10) return;
    setIsOpen(false);
    onSearch?.(digits);
  };

  const handleSelectSummary = () => {
    setIsOpen(false);
    onSearch?.(digits);
  };

  // Clearing empties the field and returns focus to the input.
  const handleClear = () => {
    requestIdRef.current++;
    setQuery('');
    setSummary(null);
    setStatus('idle');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const showSpinner = status === 'loading' || isSearching;

  return (
    <div ref={containerRef} className="relative w-full">
      <form onSubmit={handleSubmit} className="w-full">
        {/* Search field — magnifier icon, input, clear ✕ */}
        <div
          className="flex items-center h-10 w-full bg-white border border-gray-300 transition-colors focus-within:border-[--brand] focus-within:ring-2 focus-within:ring-offset-0"
          style={{ borderRadius: radii.lg, '--brand': colors.navy, '--tw-ring-color': colors.navy }}
        >
          <span className="flex items-center justify-center pl-3 text-gray-400" aria-hidden="true">
            <Search className="w-4 h-4" />
          </span>

          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => { if (status !== 'idle') setIsOpen(true); }}
            placeholder="Search phone number..."
            maxLength={14}
            aria-label="Search phone number"
            aria-expanded={isOpen}
            aria-busy={status === 'loading'}
            className="flex-1 min-w-0 h-full bg-transparent text-gray-900 placeholder-gray-400 text-sm px-3 focus:outline-none"
          />

          {showSpinner && (
            <span className="flex items-center justify-center p-1" aria-hidden="true">
              <span className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
            </span>
          )}

          {query && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear search"
              className="flex items-center justify-center p-1 mr-2 rounded-lg text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
              style={{ '--tw-ring-color': colors.navy }}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </form>

      {/* Result dropdown — summary row / loading / no-results / error */}
      {isOpen && status !== 'idle' && (
        <div
          className="absolute left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50"
          style={{ borderRadius: radii.lg }}
          role="status"
          aria-live="polite"
        >
          {status === 'loading' && (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500">
              <span className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
              Searching…
            </div>
          )}

          {status === 'found' && summary && (
            <button
              type="button"
              onClick={handleSelectSummary}
              className="flex items-center justify-between gap-3 w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors focus:outline-none focus:bg-gray-50"
            >
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-gray-900 truncate">
                  {summary.name}
                </span>
                <span className="block text-xs text-gray-500 truncate">
                  {summary.phone}
                  {summary.email ? ` · ${summary.email}` : ''}
                </span>
              </span>
              <span className="flex items-center gap-1 flex-shrink-0">
                {summary.sources.map((source) => (
                  <span
                    key={source}
                    className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-[10px] font-semibold"
                  >
                    {source}
                  </span>
                ))}
              </span>
            </button>
          )}

          {status === 'no-results' && (
            <div className="px-4 py-3 text-sm text-gray-500">
              No customer found for {query}
            </div>
          )}

          {status === 'error' && (
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <span className="text-sm text-red-600">Lookup failed. Please try again.</span>
              <button
                type="button"
                onClick={() => runLookup(digits)}
                className="text-xs font-semibold text-gray-700 hover:text-gray-900 underline focus:outline-none"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderSearchBox;
